import React, { useState } from 'react';
import { HandHeart, X } from 'lucide-react';
import { toast } from 'sonner';
import { sendBuddyNudge, markNudgesSeen } from '../../lib/programsApi';

const stateLine = (b, dayLabel) => b.state === 'completed' ? `${b.firstName} completed this journey 🌿`
  : b.state === 'in_progress' ? `${b.firstName} is on this journey with you${b.daysCompleted ? ` · ${b.daysCompleted} ${dayLabel.toLowerCase()}${b.daysCompleted === 1 ? '' : 's'} completed` : ''}`
    : `${b.firstName} hasn't started yet — no rush`;

export const JourneyBuddies = ({ slug, dayLabel, data, onChange }) => {
  const [picking, setPicking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const buddies = data?.items || [];
  const nudges = data?.nudges || [];
  const presets = data?.presets || [];
  if (!buddies.length) return null;
  const send = async (pairToken, presetId) => {
    setBusy(true);
    try { await sendBuddyNudge(slug, pairToken, presetId); toast.success('Gentle nudge sent'); setPicking(null); onChange(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const dismiss = async () => { setHidden(true); try { await markNudgesSeen(slug); onChange(); } catch { /* ignore */ } };
  return (
    <div className="mb-3 space-y-2" data-testid="flagship-buddies">
      {!hidden && nudges.length > 0 && (
        <div className="rounded-2xl bg-[#D6AD60]/15 border border-[#D6AD60]/40 px-3 py-2 text-xs text-[#1e3a5f] flex gap-2 items-start" data-testid="buddy-nudge-banner">
          <HandHeart className="w-4 h-4 text-[#8a6a2a] mt-0.5 shrink-0" />
          <div className="flex-1">{nudges.map((n) => <p key={n.id} data-testid="buddy-nudge-message"><b>{n.fromFirstName}</b> {n.message}</p>)}</div>
          <button type="button" onClick={dismiss} className="w-6 h-6 rounded-full hover:bg-black/5 flex items-center justify-center" aria-label="Dismiss" data-testid="buddy-nudge-dismiss"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <div className="rounded-2xl bg-[#7a9b8e]/10 px-3 py-2 text-xs text-[#1e3a5f]">
        {buddies.map((b) => (
          <div key={b.pairToken} className="py-1" data-testid={`buddy-${b.pairToken}`}>
            <div className="flex items-center gap-2">
              <p className="flex-1">{stateLine(b, dayLabel)}</p>
              {b.nudgeSentToday ? <span className="text-[10px] text-[#7a9b8e]" data-testid={`buddy-nudged-${b.pairToken}`}>Nudge sent · again tomorrow</span>
                : <button type="button" onClick={() => setPicking(picking === b.pairToken ? null : b.pairToken)} className="text-[10px] font-semibold text-[#8a6a2a] inline-flex items-center gap-1" data-testid={`buddy-nudge-${b.pairToken}`}><HandHeart className="w-3 h-3" /> Send a gentle nudge</button>}
            </div>
            {picking === b.pairToken && (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="buddy-nudge-presets">
                {presets.map((p) => <button key={p.id} type="button" disabled={busy} onClick={() => send(b.pairToken, p.id)} className="px-2.5 h-7 rounded-full bg-white/80 border border-[#e3d2a8]/70 text-[11px] text-[#0f1e33] disabled:opacity-50" data-testid={`buddy-nudge-preset-${p.id}`}>{p.text}</button>)}
              </div>
            )}
          </div>
        ))}
        <p className="text-[10px] text-[#7a9b8e] mt-0.5">We're doing this together — only high-level updates are shared. One gentle nudge a day.</p>
      </div>
    </div>
  );
};
